import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "../../app/helpers";
import { clearFloors } from "../../features/Booking-Form/slices/booking.slice";
import { VisitorRegistrationEntry } from "../../features/Booking-Form/typings/booking-inputs";
import { LocationInventory } from "../../features/FloorManager/typings/location-inventory";
import { useRemoteFetchCompanyLocations } from "../../hooks/Remote/Location/useRemoteFetchCompanyLocations";

/** Used by {@link VisitorVisitOnlyLocationAutocomplete} and covered directly in unit tests. */
export function visitOnlyLocationInventoryOptionEqual(
  a: LocationInventory,
  b: LocationInventory
): boolean {
  return a.id === b.id;
}

type P = {
  entry: VisitorRegistrationEntry;
  onPatch: (id: string, patch: Partial<VisitorRegistrationEntry>) => void;
  /** When false, user cannot change the location (e.g. visit type is not "visit only"). */
  disabled?: boolean;
};

/**
 * Per-visitor location for "visit only" (no desk/room booking). Updates the visitor row only.
 */
export function VisitorVisitOnlyLocationAutocomplete({
  entry,
  onPatch,
  disabled = false
}: Readonly<P>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedId = entry.visitOnlySelectedLocation;

  const { data: companyLocations, isLoading: companyLocationsLoading } =
    useRemoteFetchCompanyLocations({
      isFinanceRequest: false,
      noMobileWorking: true,
      isBookingSettings: true
    });

  const value = useMemo(() => {
    if (selectedId == null || !companyLocations?.length) {
      return null;
    }
    return companyLocations.find(loc => loc.id === selectedId) ?? null;
  }, [companyLocations, selectedId]);

  const applyLocation = (loc: LocationInventory | null) => {
    if (loc) {
      onPatch(entry.id, {
        visitOnlySelectedLocation: loc.id,
        visitOnlyLocationName: loc.name,
        visitOnlyIsMobileWorking: loc.isMobileWorking,
        visitOnlyTimezone: loc.timezone
      });
    } else {
      onPatch(entry.id, {
        visitOnlySelectedLocation: null,
        visitOnlyLocationName: null,
        visitOnlyIsMobileWorking: false,
        visitOnlyTimezone: ""
      });
    }
    dispatch(clearFloors());
  };

  return (
    <Autocomplete<LocationInventory, false, false, false>
      data-testid={`visitor-visit-only-location-autocomplete-${entry.id}`}
      disabled={disabled}
      fullWidth
      getOptionLabel={option => option.name}
      isOptionEqualToValue={visitOnlyLocationInventoryOptionEqual}
      loading={companyLocationsLoading}
      noOptionsText={t("No Options")}
      onChange={(_, loc) => {
        applyLocation(loc);
      }}
      options={companyLocations ?? []}
      value={value}
      renderInput={params => (
        <TextField
          {...params}
          required={!disabled}
          label={t("Location")}
          placeholder={t("_visitorBooking_locationPlaceholder")}
          variant={"outlined"}
        />
      )}
    />
  );
}
